import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CalendarDays, Package } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const initial = {
  name: "",
  email: "",
  phone: "",
  quantity: "",
  product_interest: "",
  delivery_date: "",
  notes: "",
};

export const Reservations = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.quantity || !form.product_interest || !form.delivery_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/reservations`, form);
      toast.success("Reservation received", {
        description: "Our team will confirm within 24 hours.",
      });
      setForm(initial);
    } catch {
      toast.error("Could not submit reservation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="reservations"
      data-testid="reservations-section"
      className="py-20 md:py-28 bg-secondary/40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14 items-start">
        <div>
          <div className="font-mono-ui text-xs uppercase tracking-widest text-accent mb-3">
            — 02 / Bulk & Standing Orders
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-semibold leading-tight tracking-tight">
            Reserve a batch{" "}
            <em className="italic text-accent font-medium">before</em> it's pressed.
          </h2>
          <p className="mt-6 text-muted-foreground max-w-lg leading-relaxed">
            Buying for a restaurant, a temple, or a large family? Tell us what
            you need and by when. We'll set aside the batch from the next
            press-run.
          </p>

          <div className="mt-10 space-y-6">
            {[
              {
                icon: Package,
                title: "Wholesale pricing",
                copy: "From 5 kg / 5 L upward. Custom pricing tiers per product.",
              },
              {
                icon: CalendarDays,
                title: "Standing orders",
                copy: "Weekly or fortnightly deliveries. Set it once, we handle the rest.",
              },
            ].map((item, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-11 h-11 rounded-full bg-accent flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-accent-foreground" />
                </div>
                <div>
                  <div className="font-display text-lg font-semibold">
                    {item.title}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{item.copy}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="reservation-form"
          className="bg-card border border-border rounded-2xl p-6 md:p-8 space-y-5 shadow-lg"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-ui">Full name</Label>
              <Input
                data-testid="reserve-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Ravi Patel"
                className="mt-2 h-11"
                required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-ui">Phone</Label>
              <Input
                data-testid="reserve-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+91 93973 63140"
                className="mt-2 h-11"
                required
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui">Email</Label>
            <Input
              data-testid="reserve-email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="you@kitchen.com"
              className="mt-2 h-11"
              required
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-ui">Interested in</Label>
              <Select
                value={form.product_interest}
                onValueChange={(v) => update("product_interest", v)}
              >
                <SelectTrigger data-testid="reserve-product" className="mt-2 h-11">
                  <SelectValue placeholder="Choose category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="oil">Cold-pressed oils</SelectItem>
                  <SelectItem value="ghee">Bilona ghee</SelectItem>
                  <SelectItem value="milk">Farm milk & curd</SelectItem>
                  <SelectItem value="makhan">Fresh makhan</SelectItem>
                  <SelectItem value="vegetables">Fresh vegetables</SelectItem>
                  <SelectItem value="spices">Whole spices</SelectItem>
                  <SelectItem value="seeds">Raw seeds</SelectItem>
                  <SelectItem value="laddu">Festive laddu</SelectItem>
                  <SelectItem value="mixed">Mixed pantry</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-ui">Quantity</Label>
              <Input
                data-testid="reserve-quantity"
                value={form.quantity}
                onChange={(e) => update("quantity", e.target.value)}
                placeholder="e.g. 20 L / 10 kg"
                className="mt-2 h-11"
                required
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui">Delivery date</Label>
            <Input
              data-testid="reserve-date"
              type="date"
              value={form.delivery_date}
              onChange={(e) => update("delivery_date", e.target.value)}
              className="mt-2 h-11"
              required
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui">Notes</Label>
            <Textarea
              data-testid="reserve-notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Anything else we should know?"
              className="mt-2 min-h-[90px]"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="reserve-submit"
            className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-sm font-semibold"
          >
            {submitting ? "Reserving..." : "Reserve batch"}
          </Button>
        </form>
      </div>
    </section>
  );
};
